import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Table, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

// Services
import { agentService } from '../services/agentService';

const MyBankCardPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [editingCard, setEditingCard] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        accountName: '',
        accountNumber: ''
    });

    const fetchWallets = useCallback(async () => {
        try {
            const data = await agentService.getMyWallets();
            if (data.success) {
                setCards(data.bankCards);
            }
        } catch (error) {
            toast.error(t('error_history_fetch', 'Failed to fetch bank cards'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchWallets();
    }, [fetchWallets]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (card) => {
        setEditingCard(card);
        setFormData({
            accountName: card.accountName,
            accountNumber: card.accountNumber
        });
        setShowModal(true);
    };

    const handleAdd = () => {
        setEditingCard(null);
        setFormData({
            accountName: '',
            accountNumber: ''
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            let response;
            if (editingCard) {
                response = await agentService.updateMyBankCard(editingCard.id, formData);
            } else {
                response = await agentService.addMyBankCard(formData);
            }

            if (response.success) {
                toast.success(editingCard ? t('msg_update_success', 'Updated successfully') : t('msg_save_success', 'Bank card added successfully'));
                setShowModal(false);
                setFormData({ accountName: '', accountNumber: '' });
                setEditingCard(null);
                fetchWallets();
            }
        } catch (error) {
            toast.error(t(error.message || 'error_process_request'));
        } finally {
            setSubmitLoading(false);
        }
    };


    return (
        <div className="majestic-page-wrapper p-4">
            <div className="d-flex align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.15)' }}>
                <Button variant="link" className="p-0 text-white me-3" onClick={() => navigate('/my-wallet')}>
                    <i className="bi bi-arrow-left fs-4"></i>
                </Button>
                <div>
                    <h4 className="mb-0 text-white fw-bold">{t('my_bank_cards')}</h4>
                    <small className="text-secondary">{t('manage_bank_cards', 'Manage your linked bank cards')}</small>
                </div>
                <Button variant="warning" className="ms-auto rounded-pill px-4 fw-bold shadow-sm" onClick={handleAdd}>
                    <i className="bi bi-plus-lg me-2"></i>{t('add_card')}
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="warning" /></div>
            ) : cards.length === 0 ? (
                <div className="text-center py-5" style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                    <i className="bi bi-credit-card text-secondary opacity-50" style={{ fontSize: '48px' }}></i>
                    <div className="text-secondary mt-3">{t('no_bank_cards_linked', 'No bank cards linked yet.')}</div>
                </div>
            ) : (
                <div className="majestic-table-wrapper rounded-3" style={{ border: '1px solid rgba(212, 175, 55, 0.15)', background: 'linear-gradient(145deg, rgba(26,26,26,0.9), rgba(13,13,13,0.95))', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table hover variant="dark" className="mb-0 align-middle bg-transparent">
                        <thead style={{ background: 'rgba(0,0,0,0.4)' }}>
                            <tr style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}>
                                <th className="py-3 ps-4 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37' }}>
                                    <i className="bi bi-person-badge me-2 opacity-50"></i>{t('cardholder_name', 'Cardholder Name')}
                                </th>
                                <th className="py-3 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37' }}>
                                    <i className="bi bi-credit-card me-2 opacity-50"></i>{t('card_number', 'Card Number')}
                                </th>
                                <th className="py-3 text-uppercase fw-bold text-end pe-4" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37', width: '100px' }}>
                                    {t('actions', 'Actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {cards.map((card, idx) => (
                                <tr key={card.id} style={{ borderBottom: idx < cards.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', transition: 'background 0.2s' }}>
                                    <td className="ps-4 py-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded-2 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', background: 'rgba(212, 175, 55, 0.1)' }}>
                                                <i className="bi bi-person-fill text-warning"></i>
                                            </div>
                                            <span className="text-white fw-medium">{card.accountName}</span>
                                        </div>
                                    </td>
                                    <td className="py-3 font-monospace" style={{ letterSpacing: '2px', color: 'rgba(255,255,255,0.7)' }}>{card.accountNumber}</td>
                                    <td className="py-3 text-end pe-4">
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            className="rounded-pill px-3"
                                            onClick={() => handleEdit(card)}
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <i className="bi bi-pencil-square me-1"></i>{t('edit', 'Edit')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => { setShowModal(false); setEditingCard(null); }} centered contentClassName="majestic-card border-0 shadow-lg">
                <Modal.Header closeButton className="border-bottom border-light border-opacity-10 py-3">
                    <Modal.Title className="text-white fw-bold">{editingCard ? t('edit_bank_card', 'Edit Bank Card') : t('add_bank_card', 'Add Bank Card')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="p-4">
                        <div className="majestic-form-group mb-3">
                            <div className="majestic-label-row">
                                <span className="majestic-label-dot"></span>
                                <span className="majestic-label-text">
                                    {t('card_number', 'CARD NUMBER')}
                                    <span style={{ color: '#ff4d4d', marginLeft: '4px' }}>*</span>
                                </span>
                            </div>
                            <div className="majestic-input-wrapper">
                                <Form.Control
                                    id="accountNumber"
                                    name="accountNumber"
                                    value={formData.accountNumber}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="0000 0000 0000 0000"
                                    className="majestic-input-field"
                                />
                            </div>
                        </div>

                        <div className="majestic-form-group mb-3">
                            <div className="majestic-label-row">
                                <span className="majestic-label-dot"></span>
                                <span className="majestic-label-text">
                                    {t('cardholder_name', 'CARDHOLDER NAME')}
                                    <span style={{ color: '#ff4d4d', marginLeft: '4px' }}>*</span>
                                </span>
                            </div>
                            <div className="majestic-input-wrapper">
                                <Form.Control
                                    id="accountName"
                                    name="accountName"
                                    value={formData.accountName}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('enter_cardholder_name', 'Enter Cardholder Name')}
                                    className="majestic-input-field"
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top border-light border-opacity-10">
                        <Button variant="link" className="text-secondary text-decoration-none" onClick={() => { setShowModal(false); setEditingCard(null); }}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit" disabled={submitLoading} className="rounded-pill px-4">
                            {submitLoading ? t('saving') : t('save', 'SAVE')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default MyBankCardPage;
